import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Users, Globe, Mail, Building2,
    Shield, ShieldAlert, Lock, LogIn, Calendar,
    Loader2, Search, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, CheckCircle2,
    XCircle, AlertTriangle, KeyRound, UserCog, User, X,
    Fingerprint, MapPin, Home, ChevronUp, ChevronDown, AtSign,
    Clock
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";

interface ADUser {
    id: number;
    created: string;
    updated: string;
    agent_id: number;
    client_id: number;
    asset_id: number;
    ad_domain_guid: string;
    name: string;
    sam_account_name: string;
    user_principal_name: string;
    domain: string;
    office: string;
    lockout: boolean;
    account_lockout_time: string;
    object_sid: string;
    mail: string;
    department: string;
    bad_logon_count: number;
    object_guid: string;
    last_logon_timestamp_pshell: string;
    last_logon_date: string;
    bad_password: string;
    password_never_expires: boolean;
    is_critical_system_object: boolean;
    password_expired: boolean;
    password_not_required: boolean;
    cannot_change_password: boolean;
    pwd_last_set_time: string;
    password_expiry_date: string;
    logon_count: number;
    created_when: string;
    modified_when: string;
    account_expire_date: string;
    account_disabled: boolean;
    member_of: string;
    display_name: string;
    distinguished_name: string;
    domain_admin: boolean;
    enterprise_admin: boolean;
    build_in_admin: boolean;
    home_drive: string;
    home_directory: string;
    description: string;
    city: string;
    country: string;
}

const parseJsonArray = (raw: string | string[]): string[] => {
    if (Array.isArray(raw)) {
        return raw;
    }

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const shortGroupName = (dn: string) => {
    const match = dn.match(/^CN=([^,]+)/);
    return match ? match[1] : dn;
};

const Val = ({ v }: { v: string | number | undefined | null }) => {
    const empty = v === undefined || v === null || v === "-" || v === "";
    return (
        <span className={empty ? " text-gray-200" : "text-gray-200"}>
            {empty ? "—" : String(v)}
        </span>
    );
};

const BoolBadge = ({
    value, trueLabel = "Yes", falseLabel = "No",
    trueColor = "text-green-400", falseColor = " text-gray-200"
}: {
    value: boolean;
    trueLabel?: string;
    falseLabel?: string;
    trueColor?: string;
    falseColor?: string;
}) => (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${value ? trueColor : falseColor}`}>
        {value ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
        {value ? trueLabel : falseLabel}
    </span>
);

// ─── Detail Panel ────────────────────────────────────────────────────────────
function UserDetailPanel({ user, onClose }: { user: ADUser; onClose: () => void }) {
    const groups = parseJsonArray(user.member_of);

    // Close on Escape key
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const Section = ({ title, icon, colorClass = "text-blue-400" }: { title: string; icon: React.ReactNode; colorClass?: string }) => (
        <div className="flex items-center gap-2 mb-3 mt-5 first:mt-0">
            <span className={colorClass}>{icon}</span>
            <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">{title}</span>
            <div className="flex-1 h-px bg-gray-700/50" />
        </div>
    );

    const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
        <div className="flex flex-col gap-0.5">
            <span className="text-[9px] text-gray-500 uppercase tracking-widest">{label}</span>
            <div className="text-[11px]">{children}</div>
        </div>
    );

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 z-40 backdrop-blur-[1px]"
                onClick={onClose}
            />

            {/* Sliding Panel */}
            <div className="fixed top-0 right-0 h-full w-[30%] min-w-[340px] bg-gray-900 border-l border-gray-700 z-55 flex flex-col shadow-2xl animate-slide-in-right">

                {/* Panel Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60 bg-gray-800/60 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                            <User size={16} className="text-blue-400" />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-white">{user.display_name !== "-" ? user.display_name : user.name}</p>
                            <p className="text-[10px] font-mono text-gray-200">{user.sam_account_name}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-gray-200 hover:text-white hover:bg-gray-700 transition-all"
                    >
                        <X size={15} />
                    </button>
                </div>

                {/* Status Pills Row */}
                <div className="flex flex-wrap gap-1.5 px-5 py-3 border-b border-gray-700/40 bg-gray-800/30 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${!user.account_disabled
                        ? "bg-green-500/10 border-green-500/20 text-green-400"
                        : "bg-red-500/10 border-red-500/20 text-red-400"
                        }`}>
                        {!user.account_disabled ? <CheckCircle2 size={9} /> : <XCircle size={9} />}
                        {!user.account_disabled ? "Active" : "Disabled"}
                    </span>
                    {user.lockout && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-yellow-500/10 border border-yellow-500/20 text-yellow-400">
                            <AlertTriangle size={9} /> Locked
                        </span>
                    )}
                    {user.domain_admin && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-red-500/10 border border-red-500/20 text-red-400">
                            <ShieldAlert size={9} /> Domain Admin
                        </span>
                    )}
                    {user.enterprise_admin && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-orange-500/10 border border-orange-500/20 text-orange-400">
                            <ShieldAlert size={9} /> Enterprise Admin
                        </span>
                    )}
                    {user.build_in_admin && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-yellow-500/10 border border-yellow-500/20 text-yellow-400">
                            <UserCog size={9} /> Built-in Admin
                        </span>
                    )}
                    {user.is_critical_system_object && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-purple-500/10 border border-purple-500/20 text-purple-400">
                            <Shield size={9} /> Critical
                        </span>
                    )}
                </div>

                {/* Scrollable Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1 text-gray-200">

                    {/* Identity */}
                    <Section title="Identity" icon={<User size={12} />} colorClass="text-blue-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Full Name"><Val v={user.display_name} /></Field>
                        <Field label="SAM Account"><span className="font-mono text-gray-300"><Val v={user.sam_account_name} /></span></Field>
                        <Field label="UPN"><span className="font-mono text-cyan-300/80 text-[10px] break-all"><Val v={user.user_principal_name} /></span></Field>
                        <Field label="Domain"><Val v={user.domain} /></Field>
                        <Field label="Mail"><span className="font-mono text-cyan-300/80"><Val v={user.mail} /></span></Field>
                        <Field label="Department"><Val v={user.department} /></Field>
                        <Field label="Office"><Val v={user.office} /></Field>
                        <Field label="Description">
                            <span className="text-gray-400 text-[10px]"><Val v={user.description} /></span>
                        </Field>
                    </div>

                    {/* Location */}
                    {(user.city !== "-" || user.country !== "-") && (
                        <>
                            <Section title="Location" icon={<MapPin size={12} />} colorClass="text-emerald-400" />
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                <Field label="City"><Val v={user.city} /></Field>
                                <Field label="Country"><Val v={user.country} /></Field>
                            </div>
                        </>
                    )}

                    {/* Password */}
                    <Section title="Password" icon={<KeyRound size={12} />} colorClass="text-amber-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Last Set"><span className="font-mono text-[10px] text-gray-300"><Val v={user.pwd_last_set_time} /></span></Field>
                        <Field label="Expiry">
                            <span className="font-mono text-[10px] text-gray-300">
                                {user.password_never_expires
                                    ? <span className="text-yellow-400 font-bold">Never Expires</span>
                                    : <Val v={user.password_expiry_date} />}
                            </span>
                        </Field>
                        <Field label="Expired"><BoolBadge value={user.password_expired} trueColor="text-red-400" falseColor=" text-gray-200" /></Field>
                        <Field label="Not Required"><BoolBadge value={user.password_not_required} trueColor="text-orange-400" falseColor=" text-gray-200" /></Field>
                        <Field label="Cannot Change"><BoolBadge value={user.cannot_change_password} trueColor="text-red-400" falseColor=" text-gray-200" /></Field>
                        <Field label="Bad Password Time"><span className="font-mono text-[10px] text-gray-300"><Val v={user.bad_password} /></span></Field>
                    </div>

                    {/* Logon Activity */}
                    <Section title="Logon Activity" icon={<LogIn size={12} />} colorClass="text-cyan-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Last Logon"><span className="font-mono text-[10px] text-gray-300"><Val v={user.last_logon_date} /></span></Field>
                        <Field label="Logon Count"><span className="text-gray-200 font-bold">{user.logon_count}</span></Field>
                        <Field label="Bad Logon Count">
                            <span className={user.bad_logon_count > 0 ? "text-red-400 font-bold" : " text-gray-200"}>
                                {user.bad_logon_count}
                            </span>
                        </Field>
                        <Field label="Account Expires"><span className="text-gray-300 text-[10px]"><Val v={user.account_expire_date} /></span></Field>
                        <Field label="Lockout Time"><span className="font-mono text-[10px] text-gray-300"><Val v={user.account_lockout_time} /></span></Field>
                    </div>

                    {/* Timestamps */}
                    <Section title="Timestamps" icon={<Calendar size={12} />} colorClass="text-purple-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Created"><span className="font-mono text-[10px] text-gray-300"><Val v={user.created_when} /></span></Field>
                        <Field label="Modified"><span className="font-mono text-[10px] text-gray-300"><Val v={user.modified_when} /></span></Field>
                    </div>

                    {/* Home */}
                    {(user.home_directory !== "-" || user.home_drive !== "-") && (
                        <>
                            <Section title="Home" icon={<Home size={12} />} colorClass="text-orange-400" />
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                <Field label="Drive"><Val v={user.home_drive} /></Field>
                                <Field label="Directory"><span className="font-mono text-[10px] break-all text-gray-300"><Val v={user.home_directory} /></span></Field>
                            </div>
                        </>
                    )}

                    {/* Identifiers */}
                    <Section title="Identifiers" icon={<Fingerprint size={12} />} colorClass="text-rose-400" />
                    <div className="flex flex-col gap-3">
                        <Field label="Object SID">
                            <span className="font-mono text-[10px] text-purple-300/80 break-all"><Val v={user.object_sid} /></span>
                        </Field>
                        <Field label="Object GUID">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={user.object_guid} /></span>
                        </Field>
                        <Field label="Distinguished Name">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={user.distinguished_name} /></span>
                        </Field>
                    </div>

                    {/* Group Memberships */}
                    <Section title={`Group Memberships (${groups.length})`} icon={<Users size={12} />} colorClass="text-indigo-400" />
                    {groups.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                            {groups.map((g, i) => (
                                <span key={i} className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-[9px] font-bold text-blue-300">
                                    {shortGroupName(g)}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className=" text-gray-200 text-[10px]">No group memberships</span>
                    )}

                    {/* bottom spacing */}
                    <div className="h-6" />
                </div>
            </div>
        </>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function ADUsers({ asset_id }: { asset_id: any }) {
    const [users, setUsers] = useState<ADUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedUser, setSelectedUser] = useState<ADUser | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const level = useClientLevel();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;

            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { sam_account_name: { $ilike: `%${debouncedSearch}%` } },
                    { user_principal_name: { $ilike: `%${debouncedSearch}%` } },
                    { domain: { $ilike: `%${debouncedSearch}%` } },
                    { mail: { $ilike: `%${debouncedSearch}%` } },
                    { department: { $ilike: `%${debouncedSearch}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch}%` } },
                    { description: { $ilike: `%${debouncedSearch}%` } },
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/ad_users", { params });

            if (response.data.status) {
                setUsers(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            } else {
                showError("Failed to load AD users");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [level, debouncedSearch, currentPage, limit, sortConfig, asset_id]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, icon }: { label: string; sortKey: string; icon?: React.ReactNode }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group select-none"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    {label}
                    <div className="flex items-center ml-auto">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-blue-400" />
                            ) : (
                                <ChevronDown size={12} className="text-blue-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Sliding Detail Panel */}
            {selectedUser && (
                <UserDetailPanel
                    user={selectedUser}
                    onClose={() => setSelectedUser(null)}
                />
            )}

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-600/20 p-1.5 rounded-lg border border-blue-500/30">
                        <Users size={16} className="text-blue-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">AD Users</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-blue-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search users..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchUsers}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <SortableHeader label="User" sortKey="name" icon={<User size={13} className="text-blue-400" />} />
                                <SortableHeader label="UPN" sortKey="user_principal_name" icon={<AtSign size={13} className="text-teal-400" />} />
                                <SortableHeader label="Domain" sortKey="domain" icon={<Globe size={13} className="text-indigo-400" />} />
                                <SortableHeader label="Mail" sortKey="mail" icon={<Mail size={13} className="text-cyan-400" />} />
                                <SortableHeader label="Dept" sortKey="department" icon={<Building2 size={13} className="text-emerald-400" />} />
                                <th className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                    <div className="flex items-center gap-2"><Shield size={13} className="text-yellow-400" /> Roles</div>
                                </th>
                                <SortableHeader label="Status" sortKey="account_disabled" icon={<Lock size={13} className="text-red-400" />} />
                                <SortableHeader label="Password" sortKey="password_never_expires" icon={<KeyRound size={13} className="text-purple-400" />} />
                                <SortableHeader label="Pwd Last Set" sortKey="pwd_last_set_time" icon={<KeyRound size={13} className="text-amber-400" />} />
                                <SortableHeader label="Pwd Expiry" sortKey="password_expiry_date" icon={<Calendar size={13} className="text-orange-400" />} />
                                <SortableHeader label="Last Logon" sortKey="last_logon_date" icon={<LogIn size={13} className="text-cyan-400" />} />
                                <SortableHeader label="Created" sortKey="created_when" icon={<Calendar size={13} className="text-pink-400" />} />
                                <SortableHeader label="Modified" sortKey="modified_when" icon={<Calendar size={13} className="text-purple-400" />} />
                                <SortableHeader label="Account Expiry" sortKey="account_expire_date" icon={<Clock size={13} className="text-rose-400" />} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={14} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading users...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={14} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Users size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                {debouncedSearch ? `No results for "${debouncedSearch}"` : "No users found"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr
                                        key={user.id}
                                        onClick={() => setSelectedUser(user)}
                                        className={`hover:bg-gray-800/60 transition-colors cursor-pointer ${selectedUser?.id === user.id ? "bg-blue-500/5 border-l-2 border-l-blue-500" : ""
                                            }`}
                                    >
                                        {/* User */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                                                    <User size={11} className="text-blue-400" />
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-bold text-gray-100">{user.name}</p>
                                                    <p className="text-[9px] font-mono text-gray-500">{user.sam_account_name}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* UPN */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] font-mono text-cyan-300/80"><Val v={user.user_principal_name} /></span>
                                        </td>

                                        {/* Domain */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] text-gray-300"><Val v={user.domain} /></span>
                                        </td>

                                        {/* Mail */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] font-mono text-cyan-300/80"><Val v={user.mail} /></span>
                                        </td>

                                        {/* Department */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <Val v={user.department} />
                                        </td>

                                        {/* Roles */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-wrap gap-1">
                                                {user.domain_admin && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-[9px] font-bold text-red-400">
                                                        <ShieldAlert size={9} /> Domain Admin
                                                    </span>
                                                )}
                                                {user.enterprise_admin && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded text-[9px] font-bold text-orange-400">
                                                        <ShieldAlert size={9} /> Enterprise Admin
                                                    </span>
                                                )}
                                                {user.build_in_admin && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500/10 border border-yellow-500/20 rounded text-[9px] font-bold text-yellow-400">
                                                        <UserCog size={9} /> Built-in Admin
                                                    </span>
                                                )}
                                                {!user.domain_admin && !user.enterprise_admin && !user.build_in_admin && (
                                                    <span className=" text-gray-200 text-[10px]">—</span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-1">
                                                <BoolBadge
                                                    value={!user.account_disabled}
                                                    trueLabel="Active"
                                                    falseLabel="Disabled"
                                                    trueColor="text-green-400"
                                                    falseColor="text-red-400"
                                                />
                                                {user.lockout && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-yellow-400">
                                                        <AlertTriangle size={11} /> Locked
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Password Status */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-0.5">
                                                {user.password_never_expires && (
                                                    <span className="text-[9px] font-bold text-yellow-400/80">Never Expires</span>
                                                )}
                                                {user.password_expired && (
                                                    <span className="text-[9px] font-bold text-red-400">Expired</span>
                                                )}
                                                {user.password_not_required && (
                                                    <span className="text-[9px] font-bold text-orange-400/80">Not Required</span>
                                                )}
                                                {!user.password_never_expires && !user.password_expired && !user.password_not_required && (
                                                    <span className="text-[10px] text-gray-300">Normal</span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Password Last Set */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-300">
                                                <Val v={user.pwd_last_set_time} />
                                            </span>
                                        </td>

                                        {/* Password Expiry */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-300">
                                                <Val v={user.password_expiry_date} />
                                            </span>
                                        </td>

                                        {/* Last Logon */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-[10px] font-mono text-gray-300">
                                                    <Val v={user.last_logon_date} />
                                                </span>
                                                <span className="text-[9px]  text-gray-200">
                                                    {user.logon_count} logon{user.logon_count !== 1 ? "s" : ""}
                                                </span>
                                                {user.bad_logon_count > 0 && (
                                                    <span className="text-[9px] font-bold text-red-400">
                                                        {user.bad_logon_count} bad attempt{user.bad_logon_count !== 1 ? "s" : ""}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Created */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-200"><Val v={user.created_when} /></span>
                                        </td>

                                        {/* Modified */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-200"><Val v={user.modified_when} /></span>
                                        </td>

                                        {/* Account Expiry */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-200"><Val v={user.account_expire_date} /></span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1  text-gray-200">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="text-[9px] text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}